import { ThemeEvents, VariantUpdateEvent } from "@theme/events";
import { formatCurrency, getVariantDisplayPriceState } from "@theme/utilities";

class ProductBnpl extends HTMLElement {
  #abortController;
  #scope;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#scope = this.closest(".shopify-section, dialog");

    this.#scope?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#scope = undefined;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const eventProductId = event.detail.data.newProduct?.id ?? event.detail.data.productId;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (eventProductId && String(eventProductId) !== this.dataset.productId) {
      return;
    }

    const activeSellingPlanId = this.#getActiveSellingPlanId();
    const { variant: displayVariant, hasActiveSellingPlan } = getVariantDisplayPriceState(
      event.detail.resource,
      activeSellingPlanId
    );

    this.#updateProviders(displayVariant, hasActiveSellingPlan);
  };

  /**
   * @returns {string}
   */
  #getActiveSellingPlanId() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return "";

    const input = this.#scope?.querySelector(`#SellingPlan-${CSS.escape(sectionId)}`);

    return input instanceof HTMLInputElement && !input.disabled ? input.value : "";
  }

  /**
   * @param {Object|null} displayVariant
   * @param {boolean} hasActiveSubscription
   */
  #updateProviders(displayVariant, hasActiveSubscription) {
    const price = Number(displayVariant?.price);
    const hasValidVariant = displayVariant != null && Number.isFinite(price);
    const moneyFormat = window.FoxTheme?.moneyFormat || "${{amount}}";
    let hasVisibleProvider = false;

    for (const provider of this.querySelectorAll("[data-bnpl-provider]")) {
      if (!(provider instanceof HTMLElement)) continue;

      const limitCents = Number(provider.dataset.limitCents) || 0;
      const hideForPrice = hasValidVariant && limitCents > 0 && price > limitCents;
      const hideForSubscription =
        hasActiveSubscription && provider.dataset.hideOnSubscription === "true";
      const isVisible = hasValidVariant && !hideForPrice && !hideForSubscription;
      const separator = provider.querySelector("[data-bnpl-separator]");

      provider.hidden = !isVisible;
      if (separator instanceof HTMLElement) separator.hidden = !isVisible || !hasVisibleProvider;

      if (hasValidVariant) this.#updateCopy(provider, price, moneyFormat);
      if (isVisible) hasVisibleProvider = true;
    }

    this.hidden = !hasVisibleProvider;
  }

  /**
   * @param {HTMLElement} provider
   * @param {number} price
   * @param {string} moneyFormat
   */
  #updateCopy(provider, price, moneyFormat) {
    const copy = provider.querySelector("[data-bnpl-copy]");
    const template = provider.querySelector("[data-bnpl-copy-template]");
    if (!(copy instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

    const maximumTermText = provider.dataset.maximumTerm || "";
    const maximumTerm = Number(maximumTermText);
    const installmentAmount =
      maximumTerm > 0 ? formatCurrency(Math.floor(price / maximumTerm), moneyFormat) : "";
    const content = template.content.cloneNode(true);
    const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();

    while (textNode) {
      textNode.textContent = textNode.textContent
        .replaceAll("[MAX_TERM]", maximumTermText)
        .replaceAll("##", installmentAmount);
      textNode = walker.nextNode();
    }

    copy.replaceChildren(content);
  }
}

if (!customElements.get("product-bnpl")) {
  customElements.define("product-bnpl", ProductBnpl);
}
